import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { kindLabel, statusLabel, InteractionStatuses, Rights } from '@aiinterview/shared';
import { api } from '../api/client';
import { useSession } from '../api/session';
import { Button, Card, TextInput, Select, Loading, StatusPill } from '../components/ui';
import { DataTable } from '../components/DataTable';
import { SendInvites } from '../components/SendInvites';
import { SentMessages } from '../components/SentMessages';

/**
 * Who has responded to a survey or listening session, and what they said.
 *
 * No score, no verdict and no approve/reject: this is feedback, and marking it would change what
 * people are willing to say into it.
 */
interface Response {
    id: number;
    formatName: string;
    employeeCode: string;
    role: string | null;
    region: string | null;
    branch: string | null;
    state: string | null;
    status: string;
    answerCount: number;
    startedAt: string | null;
    completedAt: string | null;
}

export function Interactions({ kind }: { kind: 'Survey' | 'Listening' }) {
    const [status, setStatus] = useState('');
    const [search, setSearch] = useState('');
    const { can } = useSession();

    const plural = `${kindLabel(kind).toLowerCase()} responses`;

    const responses = useQuery({
        queryKey: ['interactions', kind, status, search],
        queryFn: () => api.get<Response[]>(
            `/api/admin/interactions?kind=${kind}`
            + `${status ? `&status=${status}` : ''}`
            + `${search ? `&search=${encodeURIComponent(search)}` : ''}`)
    });

    return (
        <div className="space-y-6">
            <Card
                heading
                title={kindLabel(kind) === 'Survey' ? 'Surveys' : 'Listening sessions'}
                lede={`Everyone who has been sent a ${kindLabel(kind).toLowerCase()}, and how far `
                    + 'they got. Open one to read what they said.'}
                action={can(Rights.Export) && (
                    <a
                        className="app-btn app-btn-secondary"
                        href={`/api/admin/interactions/export?kind=${kind}`}
                    >
                        Export
                    </a>
                )}
            >
                <div className="mb-4 flex flex-wrap gap-3">
                    <Select value={status} onChange={(e) => setStatus(e.target.value)}>
                        <option value="">Any status</option>
                        {Object.values(InteractionStatuses).map((s) => (
                            <option key={s} value={s}>{statusLabel(s)}</option>
                        ))}
                    </Select>
                    <TextInput
                        placeholder="Employee code, region or branch"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>

                {responses.isLoading ? <Loading /> : (
                    <DataTable
                        rows={responses.data ?? []}
                        rowKey={(row) => row.id}
                        empty={`No ${plural} yet.`}
                        columns={[
                            {
                                header: 'Employee',
                                sortBy: (row) => row.employeeCode,
                                cell: (row) => (
                                    <span className="app-mono font-medium">{row.employeeCode}</span>
                                )
                            },
                            { header: 'Role', sortBy: (row) => row.role, cell: (row) => row.role ?? '—' },
                            { header: 'Region', sortBy: (row) => row.region, cell: (row) => row.region ?? '—' },
                            { header: 'Branch', sortBy: (row) => row.branch, cell: (row) => row.branch ?? '—' },
                            { header: 'State', sortBy: (row) => row.state, cell: (row) => row.state ?? '—' },
                            { header: 'Format', sortBy: (row) => row.formatName, cell: (row) => row.formatName },
                            {
                                header: 'Status',
                                sortBy: (row) => row.status,
                                cell: (row) => <StatusPill kind="status" value={row.status} />
                            },
                            {
                                header: 'Answers',
                                sortBy: (row) => row.answerCount,
                                cell: (row) => row.answerCount
                            },
                            {
                                header: '',
                                className: 'text-right',
                                // Same affordance as the Interviews list: a button that says what it
                                // does, rather than a name you have to guess is a link.
                                cell: (row) => (
                                    <div className="flex justify-end">
                                        <Link to={`${basePath(kind)}/${row.id}`}>
                                            <Button variant="secondary">
                                                <i className="bi bi-eye" aria-hidden="true" />
                                                View report
                                            </Button>
                                        </Link>
                                    </div>
                                )
                            }
                        ]}
                    />
                )}
            </Card>

            {can(Rights.Create) && <SendInvites kind={kind} />}

            {can(Rights.View) && <SentMessages kind={kind} />}
        </div>
    );
}

export const basePath = (kind: 'Survey' | 'Listening'): string =>
    kind === 'Survey' ? '/surveys' : '/listening';
